import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus } from "lucide-react";
import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Button } from "@/shared/components/ui/button";
import { FieldLabel } from "@/shared/components/ui/field";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellInput,
  ETableHead,
  ETableHeader,
  ETableOptions,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  updateMasterDataPdfCache,
  usePatchMasterDataPdfMutation,
  useShowMasterDataPdfQuery,
} from "@/services/backend/company/api/master-data-pdf";
import {
  MasterDataPdfAddressData,
  MasterDataPdfDefault,
  maxSenderLineChars,
} from "@/services/backend/company/types/master-data-pdf";

export function MasterDataPdfAddressDataCard({ id }: { id: string }) {
  const { data, error, isLoading } = useShowMasterDataPdfQuery({ id });

  return (
    <div>
      {data && <Content data={data} />}
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <CardSkeleton />}
    </div>
  );
}

function Content({ data }: { data: MasterDataPdfDefault }) {
  const [set, { error }] = useOptimisticDebouncedMutation(
    { id: data.id },
    updateMasterDataPdfCache,
    usePatchMasterDataPdfMutation(),
    // All inputs debounce themselves, so the mutation does not have to.
    { delay: 0 },
  );

  const setAddressData = (addressData: MasterDataPdfAddressData) =>
    set({ id: data.id, addressData }, { ...data, addressData });

  // The backend reports the sender line length under the field as a
  // whole, not per line, so the message belongs above both tables.
  const addressDataErrors = inputValidationErrorMessages(error, "addressData");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Adressfeld")}</CardTitle>
        <CardDescription>
          {t(
            "Die Zeilen des Adressfensters. Die Absenderzeilen stehen über dem Empfänger, wenn das Unternehmen das Dokument versendet.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        {addressDataErrors.length > 0 && (
          <ul className="text-destructive text-sm">
            {addressDataErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
        <div className="grid gap-2">
          <FieldLabel>{t("Absenderzeilen")}</FieldLabel>
          <LinesTable
            lines={data.addressData.senderLines}
            onLinesChange={(senderLines) =>
              setAddressData({ ...data.addressData, senderLines })
            }
            maxLength={maxSenderLineChars}
            emptyLabel={t("Es sind noch keine Absenderzeilen hinterlegt.")}
          />
        </div>
        <div className="grid gap-2">
          <FieldLabel>{t("Empfängerzeilen")}</FieldLabel>
          <LinesTable
            lines={data.addressData.receiverLines}
            onLinesChange={(receiverLines) =>
              setAddressData({ ...data.addressData, receiverLines })
            }
            emptyLabel={t("Es sind noch keine Empfängerzeilen hinterlegt.")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * LineRow adapts a line to what the table needs.
 *
 * The lines are stored as plain strings, but both the sortable
 * context and {@link ETableOptions} address rows by id and keep an
 * order field, so the strings are wrapped for the length of a
 * render. The position is the identity, which is why the id is
 * derived from the index: every change maps straight back to
 * strings, so the ids are rebuilt along with the rows.
 */
interface LineRow {
  id: string;
  order: number;
  text: string;
}

function toRows(lines: string[]): LineRow[] {
  return lines.map((text, index) => ({
    id: String(index),
    order: index,
    text,
  }));
}

function toLines(rows: LineRow[]): string[] {
  return rows.map((row) => row.text);
}

function LinesTable({
  lines,
  onLinesChange,
  emptyLabel,
  maxLength = undefined,
  disabled = false,
}: {
  lines: string[];
  onLinesChange: (lines: string[]) => void;
  emptyLabel: string;
  maxLength?: number;
  disabled?: boolean;
}) {
  const rows = toRows(lines);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    if (disabled) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rows.findIndex((row) => row.id === active.id);
    const newIndex = rows.findIndex((row) => row.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onLinesChange(toLines(arrayMove(rows, oldIndex, newIndex)));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={rows.map((row) => row.id)}
        strategy={verticalListSortingStrategy}
      >
        <ETable>
          <ETableHeader>
            <ETableRow>
              <ETableHead colSpan={3}>{t("Zeilen")}</ETableHead>
            </ETableRow>
          </ETableHeader>
          <ETableBody className="[&>tr:last-child>td]:border-b">
            {rows.length === 0 && (
              <ETableRow>
                <ETableCell colSpan={3} className="!py-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <span>{emptyLabel}</span>
                    <Button
                      onClick={() => onLinesChange([...lines, ""])}
                      variant="secondary"
                      size="sm"
                      disabled={disabled}
                    >
                      <Plus />
                    </Button>
                  </div>
                </ETableCell>
              </ETableRow>
            )}
            {rows.map((row, index) => (
              <Row
                key={row.id}
                row={row}
                rows={rows}
                index={index}
                onRowsChange={(next) => onLinesChange(toLines(next))}
                maxLength={maxLength}
                disabled={disabled}
              />
            ))}
          </ETableBody>
        </ETable>
      </SortableContext>
    </DndContext>
  );
}

function Row({
  row,
  rows,
  index,
  onRowsChange,
  maxLength,
  disabled,
}: {
  row: LineRow;
  rows: LineRow[];
  index: number;
  onRowsChange: (rows: LineRow[]) => void;
  maxLength?: number;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ETableRow ref={setNodeRef} style={style}>
      <ETableCell className="w-10">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="hover:bg-muted cursor-grab rounded p-1 disabled:cursor-default disabled:opacity-50"
          disabled={disabled}
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
      </ETableCell>
      <ETableCellInput
        value={row.text}
        maxLength={maxLength}
        disabled={disabled}
        onChange={(e) =>
          onRowsChange(
            rows.map((existing) =>
              existing.id === row.id
                ? { ...existing, text: e.target.value }
                : existing,
            ),
          )
        }
      />
      <ETableCell className="w-10">
        <ETableOptions
          item={row}
          rowIndex={index}
          items={rows}
          onItemsChange={onRowsChange}
          newItem={() => ({ id: `new-${row.id}`, order: 0, text: "" })}
          disabled={disabled}
        />
      </ETableCell>
    </ETableRow>
  );
}
